import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationFormContext } from '../../context';
import { DETAILS, LOCATION } from '../const';
import { PointGeometryField } from '../components/PointGeometryField';
import { LocationFormValues } from '../../types';
import { validateCoordinates } from 'kineticUtils';
import { Position } from 'types';

export const usePointGeometryState = () => {
  const { t } = useTranslation(['locations', 'common', 'maps']);
  const { values, setValues, setDirty, setError } = useContext(LocationFormContext);

  const [isPinActive, setPinActive] = useState(false);

  const geometryTextValue = useMemo(() => {
    const zeroLatLng = '0.00000';
    if (!values.geometry?.coordinates) {
      return t('maps:displayLatLng', { trimmedLat: zeroLatLng, trimmedLng: zeroLatLng });
    }
    const [lng, lat] = values.geometry.coordinates as Position;
    if (!lat || !lng) {
      return t('maps:displayLatLng', { trimmedLat: zeroLatLng, trimmedLng: zeroLatLng });
    }
    // segment
    if ('LineString' === values.geometry.type) {
      return t('maps:displayLatLng', { trimmedLat: zeroLatLng, trimmedLng: zeroLatLng });
    }
    const trimmedLat = lat.toFixed(5).toString();
    const trimmedLng = lng.toFixed(5).toString();
    //https://www.i18next.com/overview/configuration-options#languages-namespaces-resources
    // lng is the key for the language option, so we cant use it as a translation variable
    return t('maps:displayLatLng', { trimmedLat, trimmedLng });
  }, [values, t]);

  const [textValue, setTextValue] = useState(geometryTextValue);
  const [isGeometrySetByTextField, setIsGeometrySetByTextField] = useState(false);

  useEffect(() => {
    if (!values.geometry?.coordinates?.length) {
      setError(LOCATION, { geometry: 'required' });
    } else {
      setError(LOCATION, {});
    }
  }, [values.geometry, setError]);

  const handleGeometryUpdate = useCallback(
    (coordinatesArray: string[]) => {
      if (coordinatesArray.length === 2) {
        const latitudeArray = coordinatesArray[0].split(':');
        const longitudeArray = coordinatesArray[1].split(':');
        const newLatitude = +latitudeArray[latitudeArray.length - 1].trim();
        const newLongitude = +longitudeArray[longitudeArray.length - 1].trim();
        const newGeometry = validateCoordinates(newLatitude, newLongitude)
          ? { type: 'Point', coordinates: [newLongitude, newLatitude] }
          : undefined;
        setValues(
          (prev) =>
            ({
              ...prev,
              geometry: newGeometry,
            } as LocationFormValues)
        );
      } else {
        setValues((prev) => ({
          ...prev,
          geometry: undefined,
        }));
      }
    },
    [setValues]
  );

  const onGeometryInputChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setIsGeometrySetByTextField(true);
      setTextValue(event.target.value);
      setDirty(DETAILS, true);

      const coordinatesArray = event.target.value.split(',');
      handleGeometryUpdate(coordinatesArray);
    },
    [handleGeometryUpdate, setDirty]
  );

  const resetGeometryInput = useCallback(() => {
    setIsGeometrySetByTextField(false);
    setTextValue(geometryTextValue);
    setDirty(DETAILS, true);
    handleGeometryUpdate([]);
  }, [handleGeometryUpdate, geometryTextValue, setDirty]);

  useEffect(() => {
    if (isGeometrySetByTextField) {
      return;
    }
    setTextValue(geometryTextValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.geometry, t]);

  const onPinDrop = useCallback(() => {
    setIsGeometrySetByTextField(false);
  }, [setIsGeometrySetByTextField]);

  const sectionOptions = useMemo(
    () => ({
      fieldNames: ['geometry'],
      name: t('locationGeometry'),
      hashRoute: 'locationGeometry',
      sectionId: 'locationGeometry',
      children: (
        <PointGeometryField
          onChange={onGeometryInputChange}
          onClear={resetGeometryInput}
          textValue={textValue}
          isPinActive={isPinActive}
          setPinActive={setPinActive}
        />
      ),
    }),
    [isPinActive, onGeometryInputChange, resetGeometryInput, setPinActive, textValue, t]
  );

  return { onPinDrop, isPinActive, setPinActive, sectionOptions };
};
